import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { ContractsTaxonomiesDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { dateToString, isCurrentYear } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import ContractVersionUpdates from 'modules/contracts/components/ContractHistory/ContractVersionUpdates'
import { Milestone } from 'modules/contracts/components/ContractHistory/Timeline/Milestone'
import { getContractType } from 'modules/contracts/components/ContractHistory/lib/getContractType'
import { getMilestoneColor } from 'modules/contracts/components/ContractHistory/lib/timeline'
import { FLOWS } from 'modules/contracts/components/FlowSelectorModal/constants'
import calculateContractVersionUpdates from 'modules/contracts/lib/calculateContractVersionUpdates'
import { useContractHistoryDataContext } from 'modules/contracts/lib/contractHistoryDataContext'
import { ContractSchedule } from 'modules/contracts/lib/contractSchedule'
import { useLegalEntityContractData } from 'modules/contracts/lib/legalEntityContractDataContext'
import { getPreviousContractVersions } from 'modules/contracts/lib/utils'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'

import MetadataInfo from './MetadataInfo'
import StatusTag from './StatusTag'

interface Props {
  contractVersion: ContractVersion
  contractVersionSchedule: string
  isHighlighted: boolean
  sortedContractVersions: ContractVersion[]
  showChanges: boolean
}

const CollapsibleContractVersionCard = ({
  contractVersion,
  contractVersionSchedule,
  isHighlighted,
  sortedContractVersions,
  showChanges,
}: Props) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const previousContractVersion = getPreviousContractVersions(
    sortedContractVersions,
    contractVersion
  )
  const legalEntityContractData = useLegalEntityContractData()
  const {
    additionalCompensations: additionalCompensationsData,
    newAdditionalCompensations,
    customFieldValues: customFieldValuesData,
  } = useContractHistoryDataContext()
  const { additionalCompensations } = additionalCompensationsData
  const { data } = useQuery(ContractsTaxonomiesDocument, {
    variables: {
      ids: additionalCompensations.map(
        (compensation) => compensation.attributes.contracts_taxonomy_id
      ),
    },
  })
  const taxonomies = additionalCompensations
    .map((compensation) => {
      const compensationTaxonomy = data?.contracts.taxonomies.find(
        (taxo) => taxo.id === compensation.attributes.contracts_taxonomy_id
      )
      return compensationTaxonomy
    })
    .filter((taxonomy): taxonomy is NonNullable<typeof taxonomy> => taxonomy !== undefined)
  const customFieldValues = customFieldValuesData?.customFieldValues
  const updates = useMemo(
    () =>
      calculateContractVersionUpdates({
        currentContractVersion: contractVersion,
        legalEntityContractData,
        additionalCompensations,
        newAdditionalCompensations,
        customFieldValues,
        previousContractVersion,
        locale,
        taxonomies: taxonomies,
      }),
    [contractVersion.id, previousContractVersion?.id] // eslint-disable-line react-hooks/exhaustive-deps
  )
  const showScheduleTag = contractVersionSchedule !== ContractSchedule.expired

  const createdAt = contractVersion.createdAtString
  const authorAvatarUrl = contractVersion.authorAvatarUrl
  const authorName = contractVersion.authorName

  const actionType = contractVersion.attributes.action_type

  const calculateContractTitle = (actionType: string | undefined) => {
    let contractTitle
    if (actionType == FLOWS.promotion) {
      contractTitle = i18n.t('contract_versions.navigator.timeline.promotion')
      const jobTitleUpdate = updates.find((update) => update.key === 'job_title')

      if (jobTitleUpdate) {
        contractTitle = i18n.t('contract_versions.navigator.timeline.promotion_with_title', {
          newJobTitle: jobTitleUpdate.currentValue,
        })
      }
    } else {
      const contractType = getContractType(contractVersion, legalEntityContractData)
      contractTitle =
        contractType === ''
          ? i18n.t('contract_versions.navigator.timeline.updated_contract')
          : i18n.t('contract_versions.navigator.timeline.contract_type', {
              type: contractType,
            })
    }
    return contractTitle
  }

  const contractTitle = calculateContractTitle(actionType ?? undefined)

  const date = isCurrentYear(contractVersion.effectiveOn)
    ? dateToString(contractVersion.effectiveOn, 'ui_dm_long')
    : dateToString(contractVersion.effectiveOn, 'ui_dmy_long')

  return (
    <Box gap="s2" width="full">
      <Milestone
        color={getMilestoneColor(contractVersionSchedule)}
        label={date}
        /**
         * Current padding s16
         * Border 1px
         * Total: 17px
         */
        extraPadding={17}
      >
        <Box flexDirection="column" width="full" gap="s8">
          <Box flexDirection="row" alignItems="flexStart" justifyContent="spaceBetween" gap="s8">
            <Box flexDirection="row" alignItems="center" gap="s8">
              <Icon.Small icon={showChanges ? 'chevron-up' : 'chevron-down'} color="grey800" />
              <Text size="200" color="grey1000" weight="semibold">
                {contractTitle}
              </Text>
            </Box>
            {showScheduleTag && (
              <StatusTag schedule={contractVersionSchedule} isHighlighted={isHighlighted} />
            )}
          </Box>
          {authorName && (
            <MetadataInfo
              authorName={authorName}
              createdAt={createdAt}
              authorAvatarUrl={authorAvatarUrl}
            />
          )}
        </Box>
      </Milestone>
      {showChanges && (
        <ContractVersionUpdates contractVersionId={contractVersion.id} updates={updates} />
      )}
    </Box>
  )
}

export default observer(CollapsibleContractVersionCard)
